/**
 * MetabolicDashboard - PathwayView 3-column status grid (Sprint 4.1 REDO).
 *
 * Three cards (Heart / Metabolic / Inflammation), each with a status
 * ring rendered as an SVG arc using Signal DS palette tokens. The ring
 * carries:
 *   - a semantic color (warning-strong / signal-primary / status-active)
 *   - a status label ('Needs attention' / 'Improving' / 'Optimal')
 *   - a one-line metric summary
 *
 * Token map per HC2 (no inline hex):
 *   - attention  -> warning-strong (red)         -> bg-warning-strong-bg / fill #c2410c
 *   - improving  -> signal-primary (blue)        -> fill #3a5cff
 *   - optimal    -> status-active (green)        -> fill #16a34a
 *
 * SVG fills resolve through Tailwind class `fill-*` tokens that point at
 * the same `signalTokens.colors` map; no raw hex literal lives in the
 * markup. The class strings are listed verbatim in `tailwind.config.ts`
 * via `safelist` patterns derived from the token names; Tailwind picks
 * them up via the `content` glob because they appear as static class
 * strings here.
 *
 * HC5: zero em / en dashes (hyphens only).
 * HC6 a11y: each card is `role="group"` with an `aria-label` summary
 * including the title + status label so a screen reader announces
 * "Heart, status Needs attention" without depending on the visual ring.
 */
import { METABOLIC_DASHBOARD, type MetabolicStatus } from './fixtures';

/**
 * Token-driven color recipe for each status. Resolves through Tailwind
 * arbitrary-value classes that bind to `signalTokens.colors` via
 * `tailwind.config.ts`. No raw hex literals are introduced here.
 */
const STATUS_TONE: Record<
  MetabolicStatus,
  {
    ringClass: string;
    bgClass: string;
    fgClass: string;
    arcStrokeClass: string;
    arcFraction: number;
  }
> = {
  attention: {
    ringClass: 'ring-warning-strong-border',
    bgClass: 'bg-warning-strong-bg',
    fgClass: 'text-warning-strong-fg',
    arcStrokeClass: 'stroke-warning-strong',
    arcFraction: 0.35,
  },
  improving: {
    ringClass: 'ring-signal-primary-500',
    bgClass: 'bg-signal-primary-50',
    fgClass: 'text-signal-primary-700',
    arcStrokeClass: 'stroke-signal-primary-500',
    arcFraction: 0.65,
  },
  optimal: {
    ringClass: 'ring-status-active',
    bgClass: 'bg-status-active-bg',
    fgClass: 'text-status-active-fg',
    arcStrokeClass: 'stroke-status-active',
    arcFraction: 0.92,
  },
};

/**
 * Pure SVG ring. Stroke length is `arcFraction` of the full circumference
 * so the visual matches the named status. The ring is decorative; the
 * a11y label lives on the parent card.
 */
function StatusRing({
  status,
}: {
  status: MetabolicStatus;
}) {
  const tone = STATUS_TONE[status];
  const RADIUS = 32;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const dash = CIRCUMFERENCE * tone.arcFraction;
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 80 80"
      className="h-20 w-20"
    >
      {/* Track */}
      <circle
        cx="40"
        cy="40"
        r={RADIUS}
        fill="none"
        className="stroke-surface-border"
        strokeWidth="6"
      />
      {/* Active arc */}
      <circle
        cx="40"
        cy="40"
        r={RADIUS}
        fill="none"
        className={tone.arcStrokeClass}
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${CIRCUMFERENCE}`}
        transform="rotate(-90 40 40)"
      />
    </svg>
  );
}

export interface MetabolicDashboardProps {
  className?: string;
}

export function MetabolicDashboard({ className }: MetabolicDashboardProps) {
  const composed = [
    'grid grid-cols-1 gap-gutter-md md:grid-cols-3',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      role="group"
      aria-label="Metabolic dashboard - three column status overview"
      data-metabolic-dashboard="root"
      className={composed}
    >
      {METABOLIC_DASHBOARD.map((metric) => {
        const tone = STATUS_TONE[metric.status];
        return (
          <article
            key={metric.title}
            role="group"
            aria-label={`${metric.title}, status ${metric.statusLabel}`}
            data-metabolic-card={metric.status}
            className={[
              'flex flex-col items-center gap-gutter-sm rounded-signal-lg bg-surface-base p-gutter-lg shadow-signal-card ring-1 ring-inset',
              tone.ringClass,
            ].join(' ')}
          >
            <h3 className="text-signal-lg font-semibold text-text-primary">
              {metric.title}
            </h3>
            <StatusRing status={metric.status} />
            <span
              data-metabolic-status-label={metric.status}
              aria-label={`Status: ${metric.statusLabel}`}
              className={[
                'rounded-signal-pill px-gutter-md py-1 text-signal-xs font-semibold uppercase tracking-wide',
                tone.bgClass,
                tone.fgClass,
              ].join(' ')}
            >
              {metric.statusLabel}
            </span>
            <p className="text-signal-sm text-text-secondary">
              {metric.metricSummary}
            </p>
          </article>
        );
      })}
    </div>
  );
}
